import { FormControl } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import InputBase, { InputBaseProps } from '@mui/material/InputBase';
import React, { CSSProperties } from 'react';
import palette from '@ui/palette';
import Typography from '@ui/Typography';

export interface TextFieldProps extends InputBaseProps {
  id: string;
  label?: string;
  textAlign?: 'left' | 'center' | 'right';
  outlined?: boolean;
}

const useStyles = makeStyles<{ error: boolean }>({ name: 'TextField' })(
  (theme, { error }) => ({
    label: {
      color: theme.palette.text.primary,
      '&.Mui-focused': {
        color: theme.palette.text.primary,
      },
      marginBottom: theme.spacing(1),
    },
    inputBaseRoot: {
      'label + &': {
        marginTop: theme.spacing(3),
      },
    },
    input: {
      ...(theme.mixins.inputBorder as CSSProperties),
      fontSize: theme.spacing(1.5),
      color: theme.palette.common.white,
      borderWidth: 1,
      borderColor: error ? theme.palette.error.light : palette.gray_75,
      position: 'relative',
      padding: theme.spacing(1, 1),
      '&:hover:not(:disabled)': {
        borderColor: !error
          ? theme.palette.inputs.active
          : theme.palette.error.light,
      },
      '&:focus:not(:disabled)': {
        borderColor: !error
          ? theme.palette.inputs.focus
          : theme.palette.error.light,
      },
      '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
        {
          WebkitBoxShadow: '0 0 0 30px black inset !important',
          WebkitTextFillColor: theme.palette.text.primary,
        },
      '&::-webkit-calendar-picker-indicator': {
        cursor: 'pointer',
        filter: 'invert(1)',
      },
      '&:disabled': {
        cursor: 'not-allowed',
      },
    },
  })
);

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  ({ id, label, fullWidth = true, textAlign = 'left', ...props }, ref) => {
    const { classes } = useStyles({ error: Boolean(props.error) });
    return (
      <FormControl fullWidth={fullWidth}>
        {label && (
          <Typography variant="pSm" bold className={classes.label}>
            {label}
          </Typography>
        )}
        <InputBase
          ref={ref}
          id={id}
          inputProps={{ ...props.inputProps }}
          fullWidth={fullWidth}
          classes={{
            root: classes.inputBaseRoot,
            input: classes.input,
          }}
          {...props}
          suppressHydrationWarning
          error={Boolean(props.error)}
        />
      </FormControl>
    );
  }
);

export default TextField;
